<template>
    <div>
      <BenchmarksTooltip
        :show="hover"
        :positionTop="hoveredBar.top"
        :positionLeft="hoveredBar.left"
        :title="hoveredBar.title"
        :content="hoveredBar.tooltipContent"
        :diff="hoveredBar.diff"
        :theme="theme"
      />
      <ChartWrapper
        :data="data"
        :width="width"
        :height="height"
        :chartDefaults="chartDefaults"
        :chartKeys="benchmarksChartKeys"
      >
        <template #x-axis="props">
          <XAxis
            v-bind="props"
            :useShortLabel="useShortLabel"
            :textColor="chartColor"
            :lineColor="chartColor"
          />
        </template>
        <template #y-axis="props">
          <YAxis
            v-bind="props"
            :isInPercentages="isInPercentages"
            intervalStyle="stroke-dasharray: 4 2; stroke: #C1CCD4"
            :textColor="chartColor"
          />
        </template>
        <template #bars="props">
          <GroupedBars
            v-bind="props"
            :hasTransitions="hasTransitions"
            :hasHover="true"
            :barWidth="barWidth"
            :barPadding="barPadding"
            :dimension="dimension"
            :barColors="benchmarksColors"
            :chartKeys="benchmarksChartKeys"
            @updateHoveredBar="updateHoveredBar"
            @updateHover="updateHoverStatus"
          />
        </template>
      </ChartWrapper>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import BenchmarksTooltip from '../BenchmarksTooltip/BenchmarksTooltip.vue';
import ChartWrapper from '../../Common/Charting/Shared/Graph/ChartWrapper.vue';
import XAxis from '../../Common/Charting/Shared/Graph/Axes/XAxis.vue';
import YAxis from '../../Common/Charting/Shared/Graph/Axes/YAxis.vue';
import GroupedBars from '../../Common/Charting/Shared/Graph/Bars/GroupedBars.vue';

export default {
  props: {
    data: {
      type: Array,
      required: true,
    },
    theme: {
      type: String,
      default: 'category',
      validator: v => {
        return ['category', 'brand'].indexOf(v) > -1;
      },
    },
    width: {
      type: Number,
      required: true,
    },
    height: {
      type: Number,
      required: true,
    },
    labels: {
      type: Object,
      required: true,
    },
    dimension: {
      type: String,
    },
    isInPercentages: {
      type: Boolean,
      default: true,
    },
    hasTransitions: {
      type: Boolean,
      default: false,
    },
    useShortLabel: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ChartWrapper,
    BenchmarksTooltip,
    XAxis,
    YAxis,
    GroupedBars,
  },
  data() {
    return {
      chartColor: '#778699',
      barWidth: 24,
      barPadding: 8,
      hover: false,
      hoveredBar: {
        top: 0,
        left: 0,
        title: '',
        tooltipContent: [],
        diff: 0,
      },
    };
  },
  computed: {
    ...mapGetters([
      'benchmarksColors',
      'benchmarksChartKeys',
    ]),
    numberOfBars() {
      return this.benchmarksChartKeys.length;
    },
    dataWidth() {
      return (this.barWidth * this.numberOfBars)
        + (this.barPadding * this.numberOfBars - 1);
    },
    chartDefaults() {
      return {
        dataWidth: this.dataWidth,
        xLabelPadding: 16,
        yLabelPadding: 8,
        yLabelWidth: 30,
        fontSize: 12,
        margin: {
          top: 5,
          right: 0,
          bottom: 0,
          left: 10,
        },
      };
    },
  },
  methods: {
    hoveredBarValue(bar, key) {
      const missingDataMessage = this.theme === 'category'
        ? 'NON-SIG'
        : 'NO DATA';

      return bar[key] !== null
        ? bar[key].toFixed(1) + (this.isInPercentages ? '%' : '')
        : missingDataMessage;
    },
    generateTooltipContent(bar) {
      return this.benchmarksChartKeys.reduce((acc, key) => {
        acc.push({
          label: this.labels[key.name],
          value: this.hoveredBarValue(bar, key.value),
        });

        return acc;
      }, []);
    },
    updateHoveredBar(tooltipPosition) {
      const currentBar = this.data.find(d => d['dimension'] === tooltipPosition.dimension);
      const tooltipContent = this.generateTooltipContent(currentBar);

      const newHoveredBar = {
        top: tooltipPosition.top,
        left: tooltipPosition.left,
        title: currentBar.dimension,
        tooltipContent,
        diff: currentBar.diff,
      };

      this.hoveredBar = newHoveredBar;
    },
    updateHoverStatus(val) {
      this.hover = val;
    },
  },
};
</script>
